/**
 * Make an authenticated API call to your backend
 * @param {string} endpoint - API endpoint (e.g., '/api/users')
 * @param {Object} options - Fetch options
 * @param {Function} getAccessTokenSilently - Auth0 function to get token
 * @returns {Promise} - API response
 */
export const authenticatedApiCall = async (endpoint, options = {}, getAccessTokenSilently) => {
  try {
    // Get the access token from Auth0
    const token = await getAccessTokenSilently();
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    // Make the API call
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

// Base URL for your backend API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Example API functions
 */
export const createApi = (getAccessTokenSilently) => ({
  // Get user's todos
  getTodos: () => authenticatedApiCall('/api/todos', {}, getAccessTokenSilently),
  
  // Create a new todo
  createTodo: (todo) => authenticatedApiCall('/api/todos', {
    method: 'POST',
    body: JSON.stringify(todo),
  }, getAccessTokenSilently),
  
  // Update a todo
  updateTodo: (id, todo) => authenticatedApiCall(`/api/todos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(todo),
  }, getAccessTokenSilently),
  
  // Delete a todo
  deleteTodo: (id) => authenticatedApiCall(`/api/todos/${id}`, {
    method: 'DELETE',
  }, getAccessTokenSilently),
  
  // Get user profile
  getUserProfile: () => authenticatedApiCall('/api/profile', {}, getAccessTokenSilently),
}); 